import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useControlEvidence,
  useDeleteEvidence,
} from '@/api/hooks/useCompliance';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { EvidenceCaptureModal } from './EvidenceCaptureModal';
import type { EvidenceItem } from '@/api/types/compliance.types';

interface EvidencePanelProps {
  controlId: string;
  controlRef?: string;
  controlTitle?: string;
  layout?: 'side-panel' | 'page';
}

export function EvidencePanel({ controlId, controlRef, controlTitle, layout = 'side-panel' }: EvidencePanelProps) {
  const { data: evidence, isLoading } = useControlEvidence(controlId);
  const deleteMutation = useDeleteEvidence(controlId);
  const [addOpen, setAddOpen] = useState(false);
  // Deny-check (mirrors the API route gate): view-only (executive_viewer) can't upload.
  // Any other role — including future ones — can, so the affordance won't silently rot.
  const { roles } = useAuthStore();
  const canUpload = (roles ?? []).some((r) => r !== 'executive_viewer');

  const evidenceList: EvidenceItem[] = Array.isArray(evidence) ? evidence : [];

  return (
    <div className={cn('space-y-3', layout === 'page' && 'space-y-4')}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Evidence</p>
        <div className="flex gap-2">
          {/* AUTO-COLLECT RETIRED 2026-04-19. Feature was producing evidence with no
             underlying artefact, no analyst-review gate, silently inflating readiness
             scores, and creating pol_control_implementations rows at status=not_started
             as a side-effect. Backend service (evidence-collector.service.ts) retained
             intact for eventual rebuild. See DECISIONS.md 2026-04-19 section and
             backlog #85. */}
          {/* D-F4-7: Upload button removed. File-attachment support tracked in backlog #66. */}
          {canUpload && (
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddOpen(true)}>
              Add Supporting Evidence
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : evidenceList.length === 0 ? (
        <p className="text-xs text-text-secondary italic">No evidence attached</p>
      ) : (
        <div className="space-y-2">
          {evidenceList.map((e) => (
            <div
              key={e.id}
              className={cn(
                'flex items-center justify-between gap-2 rounded-md border p-2.5',
                e.is_expired ? 'border-accent-rose/30 bg-accent-rose/5' : 'border-border-subtle bg-bg-surface',
              )}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-text-primary line-clamp-1">{e.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant={e.source === 'automated' ? 'sky' : 'muted'} className="text-[9px]">{e.source}</Badge>
                  <span className="text-[10px] text-text-secondary">{e.evidence_type.replace(/_/g, ' ')}</span>
                  {e.valid_until && (
                    <span className={cn('text-[10px]', e.is_expired ? 'text-accent-rose' : 'text-text-secondary')}>
                      {e.is_expired ? 'Expired' : `Valid until ${e.valid_until.substring(0, 10)}`}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() =>
                  deleteMutation.mutate(e.id, {
                    onSuccess: () => toast.success('Removed'),
                    onError: () => toast.error('Failed'),
                  })
                }
                className="text-text-secondary hover:text-accent-rose transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <EvidenceCaptureModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        controlId={controlId}
        controlRef={controlRef}
        controlTitle={controlTitle}
      />
    </div>
  );
}
